import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import {
  Button,
  Flex,
  Header,
  OneColumnLayout,
  SelectField,
} from '@electricjs/arc';
import { usePageTitle } from '@/contexts/PageTitleContext';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { FormActionsContainer } from '@/components/Onboardings/common';
import {
  useDemoSyncHRISEmployeeMutation,
  useListHrisProvidersQuery,
} from '@/redux/slices/hrisIntegrationApiSlice';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useOrganizationEmployees } from '@/hooks/useOrganizationEmployees';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { transformEmployeesToOptions } from '@/components/People/transformOptions';
import { useIsAdpWfnCanadaConnectedOrg } from '@/hooks/useIsAdpWfnCanadaConnectedOrg';
import { useShouldDisableOffboardingButton } from '@/components/Offboardings/helpers';
import { Employee } from '@/types/employees';
import {
  HrisProviderOption,
  transformHrisProvidersToOptions,
} from '@/components/HRISIntegrations/transformOptions';
import { EmployeeSelectOption } from '@/components/EmployeeSelectOption';
import type { EmployeeOption } from '@/components/People/EmployeeGroups/NewEmployeeGroupForm';

export type FormData = {
  hrisProvider: HrisProviderOption | null;
  employee: EmployeeOption | null;
};

const getFormSchema = (t: TFunction) =>
  yup.object().shape({
    hrisProvider: yup
      .object()
      .nullable()
      .shape({
        value: yup.string().required(),
        name: yup.string().required(),
        label: yup.string().required(),
      })
      .required(t('pages:onboarding.hrisOffboarding.hrisProviderRequired')),
    employee: yup
      .object()
      .nullable()
      .shape({
        value: yup.string().required(),
        label: yup.string().required(),
      })
      .required(t('pages:onboarding.hrisOffboarding.employeeRequired')),
  });

const NewHRISOffboarding = () => {
  const { t, i18n } = useTranslation(['pages', 'common', 'translation']);
  const isAdpWfnCanada = useIsAdpWfnCanadaConnectedOrg();
  const titleProps = usePageTitle(t('pages:onboarding.hrisOffboarding.title'));
  const navigate = useNavigate();
  const organizationId = useGetOrganizationId();
  const { data: providers, isLoading } = useListHrisProvidersQuery();
  const providerOptions = transformHrisProvidersToOptions(providers);

  const employees = useOrganizationEmployees();
  const employeeOptions = transformEmployeesToOptions(
    employees,
    i18n.language,
    isAdpWfnCanada
  );

  const [demoSyncHRISEmployee] = useDemoSyncHRISEmployeeMutation();

  const { showSuccessToast, showErrorToast } = useGlobalUI();

  const defaultValues = {
    employee: null as EmployeeOption | null,
  };

  const methods = useForm<FormData>({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(getFormSchema(t)),
  });
  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid },
  } = methods;
  // Watch the 'employee' form field to track the currently selected employee
  const selectedEmployeeOption = watch('employee');
  const selectedEmployeeId = selectedEmployeeOption?.value ?? '';

  const selectedEmployee = employees.find(emp => emp.id === selectedEmployeeId);
  const isOffboardingDisabled = useShouldDisableOffboardingButton(
    selectedEmployee || ({} as Employee)
  );

  const handleErrorState = (logError?: string) => {
    showErrorToast({
      id: 'demo-reate-offboarding-error-toast',
      message: t('pages:onboarding.hrisOffboarding.errorMessage'),
      stack: true,
    });

    if (logError) {
      window?.DD_RUM?.addError(logError, {
        location: 'Create Offboarding',
      });

      console.error(logError);
    }
  };

  const submitOffboarding = async (data: FormData) => {
    try {
      const employeeId = data.employee?.value;
      if (!employeeId) {
        handleErrorState('Employee not selected');
        return;
      }
      const selectedEmployee = employees.find(
        employee => employee.id === employeeId
      );

      if (!selectedEmployee) {
        handleErrorState('Employee not found');
        return;
      }

      const employeeData = {
        sourceId: selectedEmployee.id,
        firstName: selectedEmployee.firstName,
        lastName: selectedEmployee.lastName,
        jobTitle: selectedEmployee.jobTitle,
        workEmail: selectedEmployee.email,
        personalEmail: selectedEmployee.personalEmail,
        status: selectedEmployee.status,
        startDate: selectedEmployee.startDate,
        address: selectedEmployee.address,
        terminationDate: new Date().toISOString().split('T')[0],
      };

      const hrisProvider = data.hrisProvider as HrisProviderOption;

      await demoSyncHRISEmployee({
        organizationId,
        hrisProviderSlug: hrisProvider.value,
        hrisEmployee: employeeData,
        isOffboarding: true,
      }).unwrap();

      showSuccessToast({
        id: 'demo-offboard-employee-success-toast',
        message: t('pages:onboarding.hrisOffboarding.successMessage'),
        stack: true,
      });

      navigate(`/employees`);
    } catch (error) {
      handleErrorState(
        `Unable to submit offboarding HRIS employee:  ${JSON.stringify(error)}`
      );
    }
  };

  return (
    <OneColumnLayout>
      <Flex maxWidth="72rem" width="100%" margin="0 auto" vertical>
        <FormProvider {...methods}>
          <Header {...titleProps} />
          <Flex width="100%" vertical>
            <SelectField
              id="hris-providers-search-selector"
              name="hrisProvider"
              control={control}
              label={t('pages:onboarding.hrisOffboarding.hrisProviderLabel')}
              placeholder={t(
                'pages:onboarding.hrisOffboarding.hrisProviderPlaceholder'
              )}
              required
              isSearchable
              isClearable
              getOptionValue={provider => provider.id}
              getOptionLabel={provider => provider.name}
              options={providerOptions}
              isLoading={isLoading}
            />
            <SelectField<EmployeeOption>
              id="employee-search-select"
              name="employee"
              control={control}
              label={t('pages:onboarding.hrisOffboarding.employeeLabel')}
              placeholder={t(
                'pages:onboarding.hrisOffboarding.employeePlaceholder'
              )}
              required
              isSearchable
              isClearable
              getOptionValue={option => option.value}
              getOptionLabel={option => option.label}
              options={employeeOptions}
              customComponents={{
                Option: option => <EmployeeSelectOption data={option.data} />,
              }}
              filterOption={(option, inputValue) => {
                const search = inputValue.trim().toLowerCase();
                if (!search) return true;
                const data = option.data ?? option;
                const label = (option.label ?? data?.label ?? '').toLowerCase();
                const email = (data?.email ?? '').toLowerCase();
                const name = (data?.name ?? label).toLowerCase();
                return (
                  label.includes(search) ||
                  email.includes(search) ||
                  name.includes(search)
                );
              }}
            />
          </Flex>
          <FormActionsContainer>
            <Button
              id="demo-offboarding-cancel-button"
              variant="outline"
              onClick={() => navigate(-1)}>
              {t('pages:onboarding.hrisOffboarding.cancel')}
            </Button>

            <Button
              id="demo-offboarding-confirm-button"
              onClick={handleSubmit(submitOffboarding)}
              disabled={!isValid || isOffboardingDisabled}>
              {t('pages:onboarding.hrisOffboarding.submit')}
            </Button>
          </FormActionsContainer>
        </FormProvider>
      </Flex>
    </OneColumnLayout>
  );
};

export default NewHRISOffboarding;
